/**
 * 화면모드(라이트/다크) · 글자크기 — 관리자 전역 스위치
 *
 * 구조는 모바일(`F/Mobile/Theme.js`)과 같다. 다른 점 둘:
 *   1. AdminLTE 3.2 가 자체 다크(`.dark-mode`)를 번들로 갖고 있어 `data-theme` 과 **함께** 켠다.
 *      → 카드·표·폼·모달·드롭다운이 벤더 CSS 로 따라온다. 우리 sass 는 남은 자리만 덮는다.
 *   2. 'auto'(시간대 자동)가 없다 — 근무시간에 보는 화면이라 의미가 없다.
 *
 * 첫 반영은 `<head>` 부트 스크립트(App\Common\ThemeBoot)가 이미 끝냈다. 이 모듈은 그 뒤의 런타임 토글만 맡는다.
 *
 * ── 잠긴 화면 ─────────────────────────────────────────────
 * 접수·기사관리처럼 CS 성역인 페이지는 서버가 **막힌 축만** 빼고 부트 스크립트를 내리면서
 * `<html data-theme-lock="theme">` 처럼 그 축 목록을 단다 (판정 정본 = App\Common\AdminTheme).
 * 여기서는 그 목록을 보고 **축별로** 토글을 잠근다 — 저장값을 건드리면 다른 화면 설정까지 흔들린다.
 *
 * 사용법
 *   $j.F.Admin.Theme.getPref()       // 'light' | 'dark'
 *   $j.F.Admin.Theme.setTheme('dark')
 *   $j.F.Admin.Theme.setFontSize('l')
 *   $j.F.Admin.Theme.setUi('soft')       // 테마 — 둥글게 + 넉넉한 여백 + 옅은 틴트
 */
const THEMES = ['light', 'dark'];        // [0]=기본
const SIZES  = ['m', 'l', 'xl'];         // 보통 / 크게 / 아주크게
// 테마 — 모양(라운드)·여백·색감을 한 번에 정하는 묶음. [0]=기본.
// 옛 축 둘(shape 각짐/둥긂 · density 넉넉/보통/조밀)을 흡수했다. 조밀은 뺐다 — 너무 빽빽했다.
const UIS = ['standard', 'soft'];

// ⚠ 경계키 — PHP 쪽 App\Common\ThemeBoot::KEYS['ADM'] 와 값이 같아야 한다 (개명은 양쪽 동시에).
const K_THEME = 'adm:ui:theme';
const K_FS    = 'adm:ui:fontSize';
const K_UI    = 'adm:ui:look';

// AdminLTE 가 다크를 찾는 클래스. PHP: ThemeBoot::DARK_CLASS['ADM']
const DARK_CLS = 'dark-mode';

export default class Theme {
    constructor() {
        // main.js 는 모바일/악덕/PC 에서도 로드된다 — DOM 준비 후 관리자에서만 붙는다.
        $(() => {
            if (!this.enabled) return;
            this.syncMobiscroll();
            this.bindUi();
        });
    }

    /** 이 페이지가 관리자인가 (body[data-page-type='Admin']) */
    get enabled() { return !!document.body && document.body.dataset.pageType === 'Admin'; }

    /**
     * 이 화면에서 **막힌 축** 목록 — 서버가 `<html data-theme-lock="theme,fontsize">` 로 알려준다.
     * 판정 정본은 App\Common\AdminTheme::blockedAxes() 이고 여기서는 읽기만 한다.
     *
     * ⚠ 불리언이 아니라 **목록**인 것이 핵심이다. 예전엔 `locked` 하나였는데, 그러면
     *   "잠긴 화면"이 곧 "다 막힌 화면"이라 축을 하나씩 열 수가 없었다.
     *   값이 비었거나 옛 `="1"` 이면 축을 모르는 것이므로 **안전한 쪽으로** 둘 다 막는다.
     */
    get blockedAxes() { return this.blockedAxesOf(document); }

    /**
     * 임의의 문서에서 막힌 축 — iframe 에도 속성을 붙이므로 **문서마다 따로 읽어야 한다.**
     * 부모의 잠금을 iframe 에 그대로 쓰면, 잠긴 화면 안에 열린 안 잠긴 iframe 이 설정을
     * 못 받고(그 반대도) 조용히 어긋난다.
     */
    blockedAxesOf(doc) {
        const v = doc.documentElement.getAttribute('data-theme-lock');
        if (v === null) return [];
        const list = v.split(',').map(x => x.trim()).filter(Boolean);
        return (list.length && list[0] !== '1') ? list : ['theme', 'fontsize'];
    }

    /** 이 축이 막혔나 */
    isBlocked(axis) { return this.blockedAxes.indexOf(axis) !== -1; }

    getPref()         { return this._pick(THEMES, $j.Common.Store.local.get(K_THEME)); }
    getFontSizePref() { return this._pick(SIZES,  $j.Common.Store.local.get(K_FS)); }
    getUiPref()       { return this._pick(UIS,   $j.Common.Store.local.get(K_UI)); }

    setTheme(theme) {
        if (this.isBlocked('theme')) return this.getPref();
        this._save(K_THEME, this._pick(THEMES, theme));
        this.apply();
        this.syncMobiscroll();
        return this.getPref();
    }

    setFontSize(size) {
        if (this.isBlocked('fontsize')) return this.getFontSizePref();
        this._save(K_FS, this._pick(SIZES, size));
        this.apply();
        return this.getFontSizePref();
    }

    /**
     * 테마 — 모양·여백·색감을 한 번에 바꾼다.
     * 셋을 따로 고르게 하면 조합만 늘고 어느 조합도 완성된 화면이 아니어서 하나로 묶었다.
     */
    setUi(ui) {
        // 잠긴 화면도 이 축은 받는다 — 라운드·여백은 그 화면들을 깨뜨리지 않는다
        this._save(K_UI, this._pick(UIS, ui));
        this.apply();
        return this.getUiPref();
    }

    /**
     * <html data-theme/data-fs> + .dark-mode 갱신 — 런타임 토글 시.
     * 라이트·보통은 **속성을 지운다** — CSS `:root` 가 기본값이라 부트 스크립트도 그때만 붙인다.
     */
    apply() {
        this.applyTo(document);

        // ── 같은 오리진 관리자 iframe 에도 붙인다.
        //
        // iframe 은 **별개 문서**라 `<html data-theme/data-fs/data-ui>` 를 자기가 따로 갖는다.
        // 그 값은 로드 시점에 head 부트 스크립트(App\Common\ThemeBoot)가 localStorage 에서
        // 읽어 붙이므로 처음엔 맞지만, 그 뒤 헤더에서 설정을 바꿔도 부모만 갱신돼 **열어 둔
        // iframe 만 옛 모습으로 남았다.** 불러톡 서랍(adm-chat-frame)이 그랬다 —
        // 닫고 다시 열어야(= frame.src 재설정) 테마가 따라왔다.
        this.applyToFrames(document);

        // 네비바/사이드바 크기를 실측해 CSS 변수로 넘기는 곳이 있다($j.F.Admin.ChatPanel — --adm-navh/--adm-sbw).
        // zoom 이 바뀌면 실측값도 바뀌므로 재측정을 태운다.
        $(window).trigger('resize');
        this.markUi();
    }

    /**
     * 한 문서에 화면설정 속성을 붙인다 — 막힌 축은 **그 문서의** 선언을 따른다.
     *
     * ⚠ 막힌 축은 **저장값이 아니라 기본값**으로 그린다.
     *   예전엔 이 두 줄이 저장값을 그대로 읽어서, 잠긴 화면에서 열린 축(테마)을 누르면
     *   setUi → apply 를 타고 저장돼 있던 다크가 같이 켜졌다 — 서버는 안 붙였는데
     *   클릭 한 번으로 켜지니, 잠금이 새어 나가는 자리였다.
     */
    applyTo(doc) {
        const el = doc.documentElement;
        const blocked = this.blockedAxesOf(doc);
        const dark = blocked.indexOf('theme') === -1 && this.getPref() === 'dark';
        const fs = blocked.indexOf('fontsize') !== -1 ? SIZES[0] : this.getFontSizePref();

        if (dark) el.setAttribute('data-theme', 'dark');
        else      el.removeAttribute('data-theme');
        el.classList.toggle(DARK_CLS, dark);
        if (doc.body) doc.body.classList.toggle(DARK_CLS, dark);

        if (fs !== 'm') el.setAttribute('data-fs', fs);
        else            el.removeAttribute('data-fs');

        // 기본(standard)은 속성을 붙이지 않는다 — CSS :root 가 곧 현행 화면이다
        const ui = this.getUiPref();
        if (ui !== UIS[0]) el.setAttribute('data-ui', ui);
        else               el.removeAttribute('data-ui');
    }

    /**
     * 같은 오리진 **관리자** iframe 에 전파.
     *
     * 판정은 `body[data-page-type="Admin"]` 하나다 — 관리자 레이아웃(main·popup·bare)이
     * 다 붙이고 있다. 이 문턱이 필요한 이유: 관리자 안에서 열리는 프레임이 관리자 문서만은
     * 아니다. 카카오맵(`$j.F.KakaoMap`)·이미지편집기·회원 모바일화면은 자기 사이트의
     * 화면설정을 따르므로 관리자 속성을 붙이면 오히려 어긋난다. 아직 안 뜬 about:blank 도
     * 여기서 걸러진다.
     *
     * 접근 불가(다른 오리진)는 조용히 넘긴다. 중첩 iframe 은 지금 관리자에 없어 내려가지 않는다.
     */
    applyToFrames(doc) {
        doc.querySelectorAll('iframe').forEach((f) => {
            let d;
            try { d = f.contentDocument; } catch (e) { return; }   // 다른 오리진
            if (!d || !d.body || d.body.dataset.pageType !== 'Admin') return;
            this.applyTo(d);
            this.syncMobiscroll(f.contentWindow);
        });
    }

    /**
     * 서드파티 날짜/휠 피커(mobiscroll v5)를 관리자 테마에 맞춘다.
     * 기본 themeVariant='auto' 는 OS(prefers-color-scheme)를 따라가 우리 토글과 어긋난다.
     * ⚠ 전역 setOptions 이라 '누가 마지막에 부르는가' 가 곧 결과다 — 판단은 이 메서드 한 곳에서만 한다.
     */
    syncMobiscroll(win = window) {
        // iframe 은 자기 mobiscroll 전역을 따로 갖는다 — 부모만 맞추면 서랍의 날짜 피커가
        // 다크에서 흰 달력으로 뜬다. 그래서 창을 받아 그 창의 전역을 맞춘다.
        let ms;
        try { ms = win.mobiscroll; } catch (e) { return; }   // 다른 오리진
        if (!ms || !ms.setOptions) return;
        // 잠긴 문서는 다크를 안 받으므로 피커도 라이트로 둔다 (열린 문서는 저장값대로)
        const doc = win.document;
        const dark = this.blockedAxesOf(doc).indexOf('theme') === -1 && this.getPref() === 'dark';
        ms.setOptions({ themeVariant: dark ? 'dark' : 'light' });
    }

    /**
     * 헤더 유저패널의 세그먼트 버튼 — **이 화면에 실제로 적용된 값**을 표시한다.
     * 잠긴 화면에서 저장값(다크)을 켜 두면 "어둡게가 켜졌는데 화면은 밝다"로 보여 혼란스럽다.
     * 왜 고정인지는 바로 아래 안내문이 말해 준다.
     */
    markUi() {
        const t = this.isBlocked('theme')    ? THEMES[0] : this.getPref();
        const f = this.isBlocked('fontsize') ? SIZES[0]  : this.getFontSizePref();
        const u = this.getUiPref();   // 테마는 잠긴 화면에서도 실제로 걸린다
        $('#admThemeSeg button').each((i, b) => $(b).toggleClass('is-on', b.dataset.theme === t));
        $('#admFsSeg button').each((i, b) => $(b).toggleClass('is-on', b.dataset.fs === f));
        $('#admUiSeg button').each((i, b) => $(b).toggleClass('is-on', b.dataset.ui === u));
    }

    bindUi() {
        // 드롭다운 안이라 클릭이 document 로 새면 부트스트랩이 패널을 닫는다 — 여기서 끊는다.
        $('#admThemeSeg').on('click', 'button', (e) => {
            e.preventDefault(); e.stopPropagation();
            this.setTheme(e.currentTarget.dataset.theme);
        });
        $('#admFsSeg').on('click', 'button', (e) => {
            e.preventDefault(); e.stopPropagation();
            this.setFontSize(e.currentTarget.dataset.fs);
        });
        $('#admUiSeg').on('click', 'button', (e) => {
            e.preventDefault(); e.stopPropagation();
            this.setUi(e.currentTarget.dataset.ui);
        });
        this.markUi();
    }

    // 기기(localStorage)에 저장. 서버로 보내지 않는다 — 관리자 개인 브라우저 설정이다.
    _save(key, value) { $j.Common.Store.local.set(key, value); }

    _pick(list, value) { return list.indexOf(value) > -1 ? value : list[0]; }
}
